/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';

import {
  ADMIN_NAV,
  FINANCE_NAV,
  PARENT_NAV,
  SA_NAV,
  THERAPIST_NAV,
} from '../components/layout/nav-config.js';
import { PortalShell } from '../components/layout/portal-shell.js';
import { AdminAnalyticsPage } from '../pages/admin/analytics.js';
import { ClientDetailPage } from '../pages/admin/client-detail.js';
import { ClientsPage } from '../pages/admin/clients.js';
import { AdminConsultantInvoicesPage } from '../pages/admin/consultant-invoices.js';
import { AdminDashboardPage } from '../pages/admin/dashboard.js';
import { AdminFinancePage } from '../pages/admin/finance.js';
import { FollowUpsPage } from '../pages/admin/follow-ups.js';
import { AdminProformasPage } from '../pages/admin/proformas.js';
import { AdminReportsPage } from '../pages/admin/reports.js';
import { SessionRequestsPage } from '../pages/admin/session-requests.js';
import { AdminSessionsPage } from '../pages/admin/sessions.js';
import { AdminSettingsPage } from '../pages/admin/settings.js';
import { TherapistsPage } from '../pages/admin/therapists.js';
import { ClinicUsersPage } from '../pages/admin/users.js';
import { WaitlistPage } from '../pages/admin/waitlist.js';
import { ForgotPasswordPage } from '../pages/auth/forgot-password.js';
import { SetPasswordPage } from '../pages/auth/set-password.js';
import { FinanceAnalyticsPage } from '../pages/finance/analytics.js';
import { FinanceCataloguePage } from '../pages/finance/catalogue.js';
import { FinanceDashboardPage } from '../pages/finance/dashboard.js';
import { FinanceInboxPage } from '../pages/finance/finance-inbox.js';
import { FinanceInvoicesPage } from '../pages/finance/invoices.js';
import { FinancePayrollPage } from '../pages/finance/payroll.js';
import { FinanceReportsPage } from '../pages/finance/reports.js';
import { InboxPage } from '../pages/inbox.js';
import { CustomerLoginPage } from '../pages/login/customer-login.js';
import { NotFoundPage } from '../pages/not-found.js';
import { ParentBillingPage } from '../pages/parent/billing.js';
import { ParentBookPage } from '../pages/parent/book.js';
import { ParentDashboardPage } from '../pages/parent/dashboard.js';
import { ParentHomeTasksPage } from '../pages/parent/home-tasks.js';
import { ParentMessagesPage } from '../pages/parent/messages.js';
import { ParentPatientDetailsPage } from '../pages/parent/patient-details.js';
import { ParentProgressPage } from '../pages/parent/progress.js';
import { ParentReportsPage } from '../pages/parent/reports.js';
import { ParentSessionsPage } from '../pages/parent/sessions.js';
import { PlaceholderPage } from '../pages/placeholder.js';
import { SaAuditPage } from '../pages/sa/audit.js';
import { SaClinicDetailPage } from '../pages/sa/clinic-detail.js';
import { SaClinicsPage } from '../pages/sa/clinics.js';
import { SaDashboardPage } from '../pages/sa/dashboard.js';
import { SaSettingsPage } from '../pages/sa/settings.js';
import { SaStaffUsersPage } from '../pages/sa/staff-users.js';
import { SaSubscriptionsPage } from '../pages/sa/subscriptions.js';
import { SaUsagePage } from '../pages/sa/usage.js';
import { AvailabilityPage } from '../pages/therapist/availability.js';
import { TherapistClientHomeTasksPage } from '../pages/therapist/client-home-tasks.js';
import { TherapistClientIntakePage } from '../pages/therapist/client-intake.js';
import { TherapistClientsPage } from '../pages/therapist/clients.js';
import { ConsultantInvoicesPage } from '../pages/therapist/consultant-invoices.js';
import { TherapistDashboardPage } from '../pages/therapist/dashboard.js';
import { TherapistDocumentsPage } from '../pages/therapist/documents.js';
import { TherapistMessagesPage } from '../pages/therapist/messages.js';
import { TherapistNotesPage } from '../pages/therapist/notes.js';
import { PlanDetailPage } from '../pages/therapist/plan-detail.js';
import { PlansPage } from '../pages/therapist/plans.js';
import { TherapistSchedulePage } from '../pages/therapist/schedule.js';
import { SessionNotePage } from '../pages/therapist/session-note.js';
import { SessionsPage } from '../pages/therapist/sessions.js';
import { TherapistTimesheetPage } from '../pages/therapist/timesheet.js';

import { RequireCustomerAuth, RequireStaffAuth } from './guards.js';

/**
 * Route tree for both surfaces.
 *
 * - `/`                customer login (Figma 4-role picker)
 * - `/staff/login`     staff console login (no role picker)
 * - `/admin|therapist|parent|finance/*`  role portals (S2.7 shells; pages land per sprint)
 * - `/sa/*`            Super Admin console (S2.8 shell; pages land in S13)
 *
 * Inner pages are placeholders until their sprint lands — the shells, guards,
 * and nav are the S0/S2 deliverable.
 */
export const router = createBrowserRouter([
  { path: '/', element: <CustomerLoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/set-password', element: <SetPasswordPage /> },
  { path: '/staff/login', element: <Navigate to="/" replace /> },

  {
    element: <RequireCustomerAuth />,
    children: [
      {
        path: '/admin',
        element: <PortalShell portal="CLINIC_ADMIN" nav={ADMIN_NAV} />,
        children: [
          { index: true, element: <AdminDashboardPage /> },
          { path: 'inbox', element: <FinanceInboxPage /> },
          { path: 'clients', element: <ClientsPage /> },
          { path: 'clients/:id', element: <ClientDetailPage /> },
          { path: 'therapists', element: <TherapistsPage /> },
          { path: 'waitlist', element: <WaitlistPage /> },
          { path: 'users', element: <ClinicUsersPage /> },
          { path: 'sessions', element: <AdminSessionsPage /> },
          { path: 'session-requests', element: <SessionRequestsPage /> },
          { path: 'follow-ups', element: <FollowUpsPage /> },
          { path: 'finance', element: <AdminFinancePage /> },
          { path: 'proformas', element: <AdminProformasPage /> },
          { path: 'consultant-invoices', element: <AdminConsultantInvoicesPage /> },
          { path: 'reports', element: <AdminReportsPage /> },
          { path: 'analytics', element: <AdminAnalyticsPage /> },
          { path: 'settings', element: <AdminSettingsPage /> },
          { path: '*?', element: <PlaceholderPage /> },
        ],
      },
      {
        path: '/therapist',
        element: <PortalShell portal="THERAPIST" nav={THERAPIST_NAV} />,
        children: [
          { index: true, element: <TherapistDashboardPage /> },
          { path: 'inbox', element: <InboxPage /> },
          { path: 'clients', element: <TherapistClientsPage /> },
          { path: 'clients/:id/intake', element: <TherapistClientIntakePage /> },
          { path: 'clients/:id/home-tasks', element: <TherapistClientHomeTasksPage /> },
          { path: 'schedule', element: <TherapistSchedulePage /> },
          { path: 'availability', element: <AvailabilityPage /> },
          { path: 'sessions', element: <SessionsPage /> },
          { path: 'sessions/:id/note', element: <SessionNotePage /> },
          { path: 'notes', element: <TherapistNotesPage /> },
          { path: 'plans', element: <PlansPage /> },
          { path: 'plans/:id', element: <PlanDetailPage /> },
          { path: 'timesheet', element: <TherapistTimesheetPage /> },
          { path: 'invoices', element: <ConsultantInvoicesPage /> },
          { path: 'documents', element: <TherapistDocumentsPage /> },
          { path: 'messages', element: <TherapistMessagesPage /> },
          { path: '*?', element: <PlaceholderPage /> },
        ],
      },
      {
        path: '/parent',
        element: <PortalShell portal="PARENT" nav={PARENT_NAV} />,
        children: [
          { index: true, element: <ParentDashboardPage /> },
          { path: 'inbox', element: <InboxPage /> },
          { path: 'book', element: <ParentBookPage /> },
          { path: 'patient-details', element: <ParentPatientDetailsPage /> },
          { path: 'progress', element: <ParentProgressPage /> },
          { path: 'sessions', element: <ParentSessionsPage /> },
          { path: 'home-tasks', element: <ParentHomeTasksPage /> },
          { path: 'reports', element: <ParentReportsPage /> },
          { path: 'billing', element: <ParentBillingPage /> },
          { path: 'messages', element: <ParentMessagesPage /> },
          { path: '*?', element: <PlaceholderPage /> },
        ],
      },
      {
        path: '/finance',
        element: <PortalShell portal="FINANCE" nav={FINANCE_NAV} />,
        children: [
          { index: true, element: <FinanceDashboardPage /> },
          { path: 'inbox', element: <FinanceInboxPage /> },
          { path: 'proformas', element: <AdminProformasPage /> },
          { path: 'invoices', element: <FinanceInvoicesPage /> },
          { path: 'catalogue', element: <FinanceCataloguePage /> },
          { path: 'payroll', element: <FinancePayrollPage /> },
          { path: 'reports', element: <FinanceReportsPage /> },
          { path: 'analytics', element: <FinanceAnalyticsPage /> },
          { path: '*?', element: <PlaceholderPage /> },
        ],
      },
    ],
  },

  {
    element: <RequireStaffAuth />,
    children: [
      {
        path: '/sa',
        element: <PortalShell portal="STAFF" nav={SA_NAV} />,
        children: [
          { index: true, element: <SaDashboardPage /> },
          { path: 'clinics', element: <SaClinicsPage /> },
          { path: 'clinics/:id', element: <SaClinicDetailPage /> },
          { path: 'subscriptions', element: <SaSubscriptionsPage /> },
          { path: 'usage', element: <SaUsagePage /> },
          { path: 'staff-users', element: <SaStaffUsersPage /> },
          { path: 'audit', element: <SaAuditPage /> },
          { path: 'settings', element: <SaSettingsPage /> },
          { path: '*?', element: <PlaceholderPage /> },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);
